import React from "react";
import * as Icon from "react-feather";

import "./trial-balance-import-modal.css";
import WComponent from "../../../common/WComponent";
import ToolsModal from "../kit/ToolsModal";
import ToolsSelect from "../kit/ToolsSelect";
import BookkeepingService from "../../../../services/BookkeepingService";
import UtilityService from "../../../../services/UtilityService";

const COLUMN_ROLES = [
	{ value: "accountNumber", label: "Account number" },
	{ value: "accountDescription", label: "Account description" },
	{ value: "debit", label: "Debit" },
	{ value: "credit", label: "Credit" },
	{ value: "amount", label: "Signed balance" },
];

class TrialBalanceImportModal extends WComponent {
	state = {
		grid: null,
		fileName: "",
		reading: false,
		preview: null,
		error: "",
		columns: null,
		headerRowIndex: null,
		importing: false,
		imported: null,
	};

	onChooseFile = async event => {
		const file = event.target.files && event.target.files[0];
		if (!file) return;

		await this.update({ reading: true, error: "", preview: null, grid: null, columns: null, headerRowIndex: null, fileName: file.name });

		const read = await BookkeepingService.readStatementGrid({ clientId: this.props.clientId, file });
		if (read.error || !read.grid) {
			await this.update({ reading: false, error: read.error || "That file could not be read." });
			return;
		}

		await this.update({ grid: read.grid });
		await this.loadPreview({});
	};

	loadPreview = async ({ columns, headerRowIndex }) => {
		const { clientId } = this.props;
		const { grid } = this.state;
		if (!grid) return;

		await this.update({ reading: true, error: "" });
		try {
			const preview = await BookkeepingService.previewTrialBalanceImport({
				clientId,
				grid,
				headerRowIndex: headerRowIndex === undefined ? this.state.headerRowIndex : headerRowIndex,
				columns: columns === undefined ? this.state.columns : columns,
			});
			await this.update({
				reading: false,
				preview,
				columns: preview.columns,
				headerRowIndex: preview.headerRowIndex,
			});
		} catch (error) {
			await this.update({ reading: false, error: "That trial balance could not be read." });
		}
	};

	onAssignColumn = async ({ columnIndex, role }) => {
		const columns = { ...(this.state.columns || {}) };

		Object.keys(columns).forEach(assignedRole => {
			if (columns[assignedRole] === columnIndex) delete columns[assignedRole];
		});
		if (role) columns[role] = columnIndex;

		await this.update({ columns });
		await this.loadPreview({ columns });
	};

	onImport = async () => {
		const { wpfEngagementId, onImported } = this.props;
		const { preview, fileName } = this.state;

		await this.update({ importing: true, error: "" });
		try {
			const result = await BookkeepingService.commitTrialBalanceImport({
				wpfEngagementId,
				preview,
				sourceFileName: fileName,
			});
			await this.update({ importing: false, imported: result });
			if (onImported) onImported(result);
		} catch (error) {
			const said = (error && error.response && error.response.data && error.response.data.error) || "";
			await this.update({ importing: false, error: said || "That trial balance could not be imported." });
		}
	};

	renderColumnAssignment = () => {
		const { grid, columns, headerRowIndex } = this.state;
		if (!grid || grid.length === 0) return null;

		const foundAHeaderRow = typeof headerRowIndex === "number" && headerRowIndex >= 0;
		const headerRow = (foundAHeaderRow ? grid[headerRowIndex] : grid[0]) || [];
		const roleForColumn = columnIndex => {
			const assigned = Object.keys(columns || {}).find(role => columns[role] === columnIndex);
			return assigned || null;
		};

		return (
			<div className="tbi__columns" data-test="column-assignment">
				<p className="tbi__hint">
					These are the columns we understood. Correct any that are wrong before reading the figures.
				</p>
				{headerRow.map((heading, columnIndex) => (
					<div key={columnIndex} className="tbi__column-row">
						<span className="tbi__column-heading">{String(heading || "").trim() || `Column ${columnIndex + 1}`}</span>
						<ToolsSelect
							value={roleForColumn(columnIndex)}
							options={COLUMN_ROLES}
							onChange={role => this.onAssignColumn({ columnIndex, role })}
							placeholder="Not used"
							inline
						/>
					</div>
				))}
			</div>
		);
	};

	renderPreview = () => {
		const { preview } = this.state;
		if (!preview) return null;

		const unreadable = (preview.detected && preview.detected.unmatched) || [];

		return (
			<div className="tbi__preview" data-test="import-preview">
				{unreadable.length > 0 && (
					<p className="tbi__problem">{unreadable.join("; ")}.</p>
				)}

				<div className="tbi__figure-row">
					<span>Rows read</span>
					<strong>{preview.rowsRead}</strong>
				</div>
				<div className="tbi__figure-row">
					<span>Accounts that mapped</span>
					<strong>{preview.entries.length}</strong>
				</div>
				<div className="tbi__figure-row">
					<span>Debits</span>
					<strong>{UtilityService.formatCurrency(preview.debitTotal)}</strong>
				</div>
				<div className="tbi__figure-row">
					<span>Credits</span>
					<strong>{UtilityService.formatCurrency(preview.creditTotal)}</strong>
				</div>

				{preview.balances ? (
					<p className="tbi__balanced" data-test="import-balances">
						<Icon.Check size={14} /> The trial balance balances.
					</p>
				) : (
					<p className="tbi__problem" data-test="import-out-of-balance">
						Debits and credits are out by {UtilityService.formatCurrency(Math.abs(preview.difference))}. A trial
						balance that does not balance cannot be imported — the difference has to be found in the file.
					</p>
				)}

				{preview.balances && preview.unmapped.length === 0 && (
					<div className="tbi__commit">
						<button
							type="button"
							className="tk-button"
							data-test="import-commit"
							disabled={this.state.importing || !!this.state.imported}
							onClick={this.onImport}
						>
							{this.state.importing ? "Importing…" : "Use these figures for this engagement"}
						</button>
						<p className="tk-form-help">
							This replaces anything imported before, and can be undone. Nothing about the client's bookkeeping
							is changed.
						</p>
					</div>
				)}

				{this.state.imported && (
					<p className="tbi__balanced" data-test="import-done">
						<Icon.Check size={14} /> {this.state.imported.imported} balances imported
						{this.state.imported.replaced > 0 ? `, replacing ${this.state.imported.replaced} from a previous import` : ""}.
					</p>
				)}

				{preview.unmapped.length > 0 && (
					<div className="tbi__unmapped" data-test="import-unmapped">
						<p className="tbi__problem">
							{preview.unmapped.length} account{preview.unmapped.length === 1 ? "" : "s"} on this file are not
							mapped to anything. Nothing is guessed for them — map them on the client's chart and read the file
							again.
						</p>
						{preview.unmapped.slice(0, 12).map((account, index) => (
							<div key={index} className="tbi__unmapped-row">
								<span>{account.accountNumber || "(no account number)"}</span>
								<span>{account.accountDescription || ""}</span>
								<strong>{UtilityService.formatCurrency(account.amount)}</strong>
							</div>
						))}
					</div>
				)}
			</div>
		);
	};

	render() {
		const { onClose } = this.props;
		const { reading, error, grid, fileName } = this.state;

		const footer = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={onClose}>Close</button>
				</span>
			</>
		);

		return (
			<ToolsModal open={true} onClose={onClose} title="Read a trial balance" width={720} footer={footer}>
				<p className="tbi__hint">
					Read a trial balance kept somewhere else, without going through bookkeeping. Nothing is written — this
					shows what the file would produce so it can be corrected first.
				</p>

				<label className="tk-button tk-button--secondary tbi__choose">
					<Icon.Upload size={14} /> {fileName || "Choose a spreadsheet or CSV"}
					<input type="file" accept=".xlsx,.csv" data-test="trial-balance-file" onChange={this.onChooseFile} hidden />
				</label>

				{reading && <p className="tbi__hint">Reading…</p>}
				{error && <p className="tbi__problem" data-test="import-error">{error}</p>}

				{grid && this.renderColumnAssignment()}
				{this.renderPreview()}
			</ToolsModal>
		);
	}
}

export default TrialBalanceImportModal;
